import { useAuth0 } from "@auth0/auth0-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { RouterProvider } from "react-aria-components";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { AppShell, Toast } from "@/ui/components";

import { analytics } from "@/analytics";
import useAuthListener from "@/auth/useAuthListener";
import { useHostServices } from "@/hooks/useHostServices";
import getAppConfig, { isProduction } from "@liberty-fund/config";
import { useAppSelector } from "@liberty-fund/data-context/hooks";
import { AccountState } from "@liberty-fund/data-context/types";
import { DashboardIcon, DebugIcon, MagnifyingGlassIcon } from "@/ui/icons";
import { isBoardEnabled, isPeopleEnabled } from "@liberty-fund/utils/roles";
import { orderNavigationApps } from "@/utils/navigation";
import NavigationSection from "./NavigationSection";

// Lazy rather than a static import -- AppLayout renders on every route, and
// ConferenceSearchModal pulls in useSearch from @liberty-fund/graphql-client,
// which drags the *entire* generated GraphQL hooks barrel (every query and
// mutation across every module, board/honorarium/tax-forms/people/liberty-ai)
// into the eager main bundle. That's several thousand lines of generated
// code a page load never needs unless the search palette is actually opened.
const ConferenceSearchModal = lazy(() => import("./ConferenceSearchModal"));

/** Records a sidebar link click; the resulting route change is a Page Viewed. */
const trackSidebarClick = (item: string, destination: string) =>
  analytics.track("Navigation Clicked", {
    item,
    destination,
    location: "sidebar",
  });

/**
 * Chrome shared by every route — sidebar, search palette and toasts.
 *
 * This was `MainContent`, which received the active page as a `render` prop
 * from `_app.tsx`. As a react-router layout route it renders `<Outlet />`
 * instead.
 */
const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const isActive = (pathname: string) => location.pathname === pathname;
  const { user, roles } = useAppSelector<AccountState>(
    (state) => state.account,
  );

  useAuthListener();

  const [isSearchOpen, setSearchOpen] = useState(false);
  // Read by the keyboard shortcut, whose listener is registered once and so
  // can't see the current state, to tell an open from a close.
  const isSearchOpenRef = useRef(isSearchOpen);
  isSearchOpenRef.current = isSearchOpen;

  const { toasts, removeToast } = useHostServices() || {};

  const appNames = orderNavigationApps([
    ...new Set(
      roles
        ?.filter((role) => role.startsWith("portal:"))
        .map((role) => role.split(":")[1]),
    ).values(),
  ]);

  const dashboardItem = (
    <li key="__dashboard">
      <Link
        to="/"
        onClick={() => trackSidebarClick("Dashboard", "/")}
        className={`group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white text-opacity-80
     ${
       isActive("/")
         ? "bg-lf-blue-dark text-white text-opacity-100"
         : "text-indigo-200 hover:bg-lf-blue-dark hover:bg-opacity-50 hover:text-white"
     }`}
      >
        <img
          src={DashboardIcon.src}
          alt=""
          aria-hidden="true"
          className={`h-6 w-6 shrink-0 img-white`}
        />
        Dashboard
      </Link>
    </li>
  );

  const searchItem =
    isBoardEnabled(roles) || isPeopleEnabled(roles) ? (
      <li key={"__search"}>
        <div
          onClick={() => {
            setSearchOpen(true);
            analytics.track("Search Opened", { trigger: "click" });
          }}
          className={`group flex flex-row gap-x-3 justify-between rounded-md p-2 text-sm font-semibold leading-6 text-white text-opacity-80
          text-indigo-200 hover:bg-lf-blue-dark hover:bg-opacity-50 hover:text-white`}
        >
          <div className="flex flex-row gap-x-3">
            <img
              src={MagnifyingGlassIcon.src}
              alt=""
              aria-hidden="true"
              className={`h-6 w-6 shrink-0 img-white`}
            />
            Search
          </div>
          {!/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && (
            <span className="group flex flex-row self-center rounded-md ring-1 ring-indigo-400 p-1 text-xs font-semibold text-indigo-200">
              <kbd className="font-sans">
                {navigator.platform.startsWith("Mac") ? "⌘" : "Alt"}&nbsp;
              </kbd>
              <kbd className="font-sans">K</kbd>
            </span>
          )}
        </div>
      </li>
    ) : null;

  const debugItem =
    !isProduction && roles?.includes("portal:host:admin") ? (
      <li key={"__debug"}>
        <Link
          to={`/debug`}
          onClick={() => trackSidebarClick("Debug", "/debug")}
          className={`group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white text-opacity-80
          ${
            isActive(`/debug`)
              ? "bg-lf-blue-dark text-white"
              : "text-indigo-200 hover:bg-lf-blue-dark hover:bg-opacity-50 hover:text-white"
          }`}
        >
          <img
            src={DebugIcon.src}
            alt=""
            aria-hidden="true"
            className={`h-6 w-6 shrink-0 img-white`}
          />
          Debug
        </Link>
      </li>
    ) : null;

  const navSections =
    appNames?.map((appName, index) => (
      <NavigationSection
        appName={appName}
        key={`${appName}-navsection-${index}`}
      />
    )) ?? [];

  if (searchItem) {
    navSections.unshift(searchItem);
  }

  navSections.unshift(dashboardItem);

  if (debugItem) {
    navSections.push(debugItem);
  }

  const onSearchClose = useCallback(() => {
    setSearchOpen(false);
  }, [setSearchOpen]);

  // Signing out clears the Auth0 session and returns to the portal, which
  // RequireAuth then bounces back through login. Replaces the former
  // /api/auth/logout route, which additionally had to expire the ALB's
  // AWSELBAuthSessionCookie-N cookies — with the load balancer out of the auth
  // path there are no such cookies left to clear.
  const onLogout = useCallback(() => {
    // Beacon, because logout() navigates away immediately; reset() so the
    // next person to sign in on this browser isn't merged into this user.
    analytics.track("User Signed Out", undefined, { beforeUnload: true });
    analytics.reset();
    void logout({
      logoutParams: { returnTo: getAppConfig().portalUrl },
    });
  }, [logout]);

  useEffect(() => {
    // Look for Ctrl + K or Cmd + K to open the palette
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.altKey)) {
        if (!isSearchOpenRef.current) {
          analytics.track("Search Opened", { trigger: "keyboard" });
        }
        setSearchOpen((open) => !open);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  return (
    <RouterProvider navigate={navigate}>
      <Toast toasts={toasts} removeToast={removeToast}></Toast>
      <AppShell
        navSections={navSections}
        userNickname={user?.name ?? user?.nickname}
        userPicture={user?.picture}
        onLogout={onLogout}
        onLogoutPrompt={() => analytics.track("Sign Out Prompted")}
        onLogoutCancel={() => analytics.track("Sign Out Cancelled")}
        onMobileMenuOpen={() => analytics.track("Mobile Menu Opened")}
      >
        <Outlet />
      </AppShell>

      {(isBoardEnabled(roles) || isPeopleEnabled(roles)) && (
        // fallback={null} is fine -- the modal itself renders nothing
        // visible until `isOpen` is true, same as before this was lazy.
        <Suspense fallback={null}>
          <ConferenceSearchModal
            isOpen={isSearchOpen}
            onClose={onSearchClose}
            roles={roles}
          />
        </Suspense>
      )}
    </RouterProvider>
  );
};

export default AppLayout;
